import React, { useState, useRef } from 'react';
import { Search, MapPin } from 'lucide-react';
import { useDebounce } from '../../../hooks/useDebounce';
import { searchAddress } from '../../../services/addressService';

interface LocationInputProps {
  value: {
    postcode: string;
    city: string;
    address: string;
  };
  onChange: (location: { postcode: string; city: string; address: string }) => void;
}

const LocationInput: React.FC<LocationInputProps> = ({ value, onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{
    address: string;
    postcode: string;
    city: string;
  }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  
  const debouncedQuery = useDebounce(searchQuery, 300);

  React.useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedQuery.length >= 3 && !hasSelected) {
        const results = await searchAddress(debouncedQuery);
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, hasSelected]);

  const handleSelectAddress = (suggestion: typeof suggestions[0]) => {
    onChange(suggestion);
    setSearchQuery(suggestion.address);
    setShowSuggestions(false);
    setHasSelected(true);
    setIsFocused(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setHasSelected(false);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          placeholder="Rechercher une adresse..."
          className="input w-full pl-10"
          required
        />
        {(!isFocused && !searchQuery) && (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
        )}
        
        {showSuggestions && suggestions.length > 0 && isFocused && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-neutral-200">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="w-full px-4 py-2 text-left hover:bg-neutral-50 flex items-start space-x-3"
                onClick={() => handleSelectAddress(suggestion)}
              >
                <MapPin className="h-5 w-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="font-medium">{suggestion.address}</div>
                  <div className="text-sm text-neutral-500">
                    {suggestion.postcode} {suggestion.city}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Code postal
          </label>
          <input
            type="text"
            value={value.postcode}
            onChange={(e) => onChange({ ...value, postcode: e.target.value })}
            placeholder="Code postal"
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Ville
          </label>
          <input
            type="text"
            value={value.city}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
            placeholder="Ville"
            className="input w-full"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Adresse
        </label>
        <input
          type="text"
          value={value.address}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          placeholder="Numéro et nom de la rue"
          className="input w-full"
          required
        />
      </div>
    </div>
  );
};

export default LocationInput;